import { Fraction } from '@learnforge/demo-math/numeric';
import { type Expr, num, variable, add, mul, pow, equals, canonicalise } from './expr';
import { Polynomial } from './polynomial';
import { factor } from './factor';

export type Comparator = '<' | '<=' | '>' | '>=';

/**
 * Infinity sentinels for interval bounds.
 * Use unique variable names that won't be parsed as real input.
 */
export const NEGATIVE_INFINITY: Expr = variable('-∞');
export const POSITIVE_INFINITY: Expr = variable('∞');

export function isNegativeInfinity(e: Expr): boolean {
  return e.kind === 'var' && e.name === '-∞';
}

export function isPositiveInfinity(e: Expr): boolean {
  return e.kind === 'var' && e.name === '∞';
}

export interface Interval {
  start: Expr;
  end: Expr;
  openLeft: boolean;
  openRight: boolean;
}

export type SignCell = '+' | '-' | '0' | 'undef';

export interface SignChart {
  rowLabels: readonly { factor: Expr; kind: 'factor' | 'asymptote' }[];
  columnBoundaries: readonly Fraction[];
  cells: readonly (readonly SignCell[])[];
  productRow: readonly SignCell[];
}

export type InequalityStep =
  | { rule: 'move-to-one-side'; before: Expr; after: Expr; description: string }
  | { rule: 'factor'; before: Expr; after: Expr; description: string }
  | { rule: 'identify-critical-points'; points: readonly Fraction[]; description: string }
  | { rule: 'identify-asymptotes'; points: readonly Fraction[]; description: string }
  | { rule: 'evaluate-sign-chart'; chart: SignChart; description: string }
  | { rule: 'read-off-intervals'; intervals: readonly Interval[]; description: string };

export interface InequalityResult {
  intervals: Interval[];
  signChart: SignChart;
  criticalPoints: readonly { x: Fraction; kind: 'zero' | 'asymptote' }[];
  steps: InequalityStep[];
}

/**
 * Solve an inequality: lhs OP rhs (where OP is <, <=, >, >=)
 */
export function solveInequality(
  lhs: Expr,
  comparator: Comparator,
  rhs: Expr,
  varName: string = 'x',
): InequalityResult {
  const steps: InequalityStep[] = [];
  const criticalPoints: { x: Fraction; kind: 'zero' | 'asymptote' }[] = [];

  // Step 1: Check for absolute value and handle specially
  if (lhs.kind === 'func' && lhs.name === 'abs') {
    return solveAbsoluteValueInequality(lhs, comparator, rhs, varName);
  }

  // Step 2: Move to one side: D = lhs - rhs
  const diff = add(lhs, mul(num(-1), rhs));
  const diffCanonical = canonicalise(diff);

  steps.push({
    rule: 'move-to-one-side',
    before: lhs,
    after: diffCanonical,
    description: `Rearrange to ${varName} side: move rhs to left`,
  });

  // Step 3: Detect denominators (for rational inequalities)
  const { numerator, denominator } = extractNumeratorDenominator(diffCanonical, varName);

  // Step 4: Factor numerator and denominator
  const numPoly = Polynomial.fromExpr(numerator, varName);
  const denomPoly = Polynomial.fromExpr(denominator, varName);

  if (numPoly === null || denomPoly === null) {
    // Fallback: return empty result
    return {
      intervals: [],
      signChart: {
        rowLabels: [],
        columnBoundaries: [],
        cells: [],
        productRow: [],
      },
      criticalPoints: [],
      steps,
    };
  }

  const numFactored = factor(numPoly);
  const denomFactored = factor(denomPoly);

  steps.push({
    rule: 'factor',
    before: diffCanonical,
    after: buildFactoredExpr(numFactored, denomFactored, varName),
    description: 'Factor numerator and denominator',
  });

  // Step 5: Collect critical points and asymptotes
  const allBoundaries = collectBoundaries(numFactored, denomFactored, criticalPoints);

  steps.push({
    rule: 'identify-critical-points',
    points: allBoundaries.zeros,
    description: `Critical points (zeros of numerator): ${allBoundaries.zeros.map((f) => f.toString()).join(', ')}`,
  });

  if (allBoundaries.asymptotes.length > 0) {
    steps.push({
      rule: 'identify-asymptotes',
      points: allBoundaries.asymptotes,
      description: `Asymptotes (zeros of denominator): ${allBoundaries.asymptotes.map((f) => f.toString()).join(', ')}`,
    });
  }

  // Step 6: Build sign chart
  const columnBoundaries = Array.from(new Set(allBoundaries.all.map((f) => f.toString())))
    .map((s) => {
      const parsed = Fraction.parse(s);
      return parsed;
    })
    .sort((a, b) => a.cmp(b));

  const signChart = buildSignChart(
    numFactored,
    denomFactored,
    columnBoundaries,
    allBoundaries,
    varName,
  );

  steps.push({
    rule: 'evaluate-sign-chart',
    chart: signChart,
    description: 'Evaluate sign of each factor at test points',
  });

  // Step 7: Read off intervals
  const intervals = readOffIntervals(signChart, columnBoundaries, allBoundaries, comparator);

  steps.push({
    rule: 'read-off-intervals',
    intervals,
    description: `Select regions where expression is ${comparator} 0`,
  });

  return {
    intervals,
    signChart,
    criticalPoints,
    steps,
  };
}

/**
 * Handle |f| OP a cases
 */
function solveAbsoluteValueInequality(
  lhs: Expr,
  comparator: Comparator,
  rhs: Expr,
  varName: string,
): InequalityResult {
  // Extract the argument to abs()
  const absFunc = lhs as Extract<Expr, { kind: 'func' }>;
  const f = absFunc.args[0]!;

  // Get the bound (RHS)
  if (rhs.kind !== 'num') {
    // Can't handle non-numeric bounds simply
    return {
      intervals: [],
      signChart: {
        rowLabels: [],
        columnBoundaries: [],
        cells: [],
        productRow: [],
      },
      criticalPoints: [],
      steps: [],
    };
  }

  const boundValue = rhs.value;
  const a = boundValue;

  if (comparator === '<' || comparator === '<=') {
    // |f| < a or |f| <= a => -a < f < a (or -a <= f <= a)
    const innerComparator = comparator === '<' ? '<' : '<=';
    const negA = num(a.neg());

    // Solve f > -a and f < a, then intersect
    const res1 = solveInequality(f, '>', negA, varName);
    const res2 = solveInequality(f, innerComparator, rhs, varName);

    const intersection = intervalIntersection([res1.intervals, res2.intervals]);

    return {
      intervals: intersection,
      signChart: res1.signChart,
      criticalPoints: res1.criticalPoints,
      steps: res1.steps,
    };
  } else if (comparator === '>' || comparator === '>=') {
    // |f| > a or |f| >= a => f > a or f < -a (or >= / <=)
    const innerComparator = comparator === '>' ? '>' : '>=';
    const negA = num(a.neg());

    // Solve f > a and f < -a, then union
    const res1 = solveInequality(f, innerComparator, rhs, varName);
    const res2 = solveInequality(f, innerComparator === '>' ? '<' : '<=', negA, varName);

    const union = intervalUnion([res1.intervals, res2.intervals]);

    return {
      intervals: union,
      signChart: res1.signChart,
      criticalPoints: res1.criticalPoints,
      steps: res1.steps,
    };
  }

  return {
    intervals: [],
    signChart: {
      rowLabels: [],
      columnBoundaries: [],
      cells: [],
      productRow: [],
    },
    criticalPoints: [],
    steps: [],
  };
}

/**
 * Extract numerator and denominator from an expression.
 * If there's a division (reciprocal exponent), extract it properly.
 */
function extractNumeratorDenominator(
  expr: Expr,
  varName: string,
): { numerator: Expr; denominator: Expr } {
  const denomExpressions = collectDenominatorExpressions(expr, varName);

  if (denomExpressions.length === 0) {
    return { numerator: expr, denominator: num(1) };
  }

  // Build combined denominator and rebuild numerator
  let numerator = expr;
  for (const denom of denomExpressions) {
    // Remove this denominator from the numerator
    numerator = removePowFromExpr(numerator, denom, varName);
  }

  // Canonicalise numerator
  numerator = canonicalise(numerator);

  // Build combined denominator
  let denominator = denomExpressions[0]!;
  for (let i = 1; i < denomExpressions.length; i++) {
    denominator = mul(denominator, denomExpressions[i]!);
  }

  return { numerator, denominator };
}

/**
 * Remove a power expression with negative exponent from an expr
 */
function removePowFromExpr(expr: Expr, baseDenom: Expr, _varName: string): Expr {
  // If expr is a mul, remove the pow(baseDenom, negative) part
  if (expr.kind === 'mul') {
    const filtered = expr.children.filter((child) => {
      if (child.kind === 'pow' && child.exp.kind === 'num' && child.exp.value.num < 0) {
        if (equals(child.base, baseDenom)) {
          return false; // Remove this term
        }
      }
      return true;
    });

    if (filtered.length === 0) return num(1);
    if (filtered.length === 1) return filtered[0]!;
    return mul(...filtered);
  }

  return expr;
}

/**
 * Collect expressions that appear in denominators (pow with negative exponent)
 */
function collectDenominatorExpressions(expr: Expr, varName: string): Expr[] {
  const denoms: Expr[] = [];
  const seen = new Set<string>();

  function visit(e: Expr): void {
    switch (e.kind) {
      case 'num':
      case 'var':
        break;
      case 'add':
      case 'mul': {
        for (const child of e.children) {
          visit(child);
        }
        break;
      }
      case 'pow': {
        // Check if exponent is negative (indicates denominator)
        if (e.exp.kind === 'num' && e.exp.value.num < 0) {
          const baseStr = exprToString(e.base);
          if (!seen.has(baseStr) && containsVar(e.base, varName)) {
            seen.add(baseStr);
            denoms.push(e.base);
          }
        }
        visit(e.base);
        visit(e.exp);
        break;
      }
      case 'func': {
        for (const arg of e.args) {
          visit(arg);
        }
        break;
      }
      case 'sigma': {
        visit(e.from);
        visit(e.to);
        visit(e.body);
        break;
      }
    }
  }

  visit(expr);
  return denoms;
}

/**
 * Check if an expression contains the variable
 */
function containsVar(expr: Expr, varName: string): boolean {
  switch (expr.kind) {
    case 'num':
      return false;
    case 'var':
      return expr.name === varName;
    case 'add':
    case 'mul':
      return expr.children.some((c) => containsVar(c, varName));
    case 'pow':
      return containsVar(expr.base, varName) || containsVar(expr.exp, varName);
    case 'func':
      return expr.args.some((a) => containsVar(a, varName));
    case 'sigma':
      return (
        containsVar(expr.from, varName) ||
        containsVar(expr.to, varName) ||
        containsVar(expr.body, varName)
      );
  }
}

/**
 * Convert an Expr to a string for deduplication
 */
function exprToString(expr: Expr): string {
  switch (expr.kind) {
    case 'num':
      return `num(${expr.value.toString()})`;
    case 'var':
      return `var(${expr.name})`;
    case 'add':
      return `add(${expr.children.map(exprToString).join(',')})`;
    case 'mul':
      return `mul(${expr.children.map(exprToString).join(',')})`;
    case 'pow':
      return `pow(${exprToString(expr.base)},${exprToString(expr.exp)})`;
    case 'func':
      return `func(${expr.name},${expr.args.map(exprToString).join(',')})`;
    case 'sigma':
      return `sigma(${expr.index},${exprToString(expr.from)},${exprToString(expr.to)},${exprToString(expr.body)})`;
  }
}

/**
 * Build a factored representation of the expression (for display)
 */
function buildFactoredExpr(
  numFactored: ReturnType<typeof factor>,
  denomFactored: ReturnType<typeof factor>,
  _varName: string,
): Expr {
  // Build factored expression from numerator factors and denominator factors
  const numTerms: Expr[] = [];

  // Add each numerator factor raised to its multiplicity
  for (const factor_ of numFactored.factors) {
    const factorExpr = factor_.factor.toExpr();
    if (factor_.multiplicity === 1) {
      numTerms.push(factorExpr);
    } else {
      numTerms.push(pow(factorExpr, num(factor_.multiplicity)));
    }
  }

  // Add content as a factor
  if (!numFactored.content.equals(new Fraction(1, 1))) {
    numTerms.unshift(num(numFactored.content));
  }

  // Build numerator expression
  let numeratorExpr: Expr;
  if (numTerms.length === 0) {
    numeratorExpr = num(1);
  } else if (numTerms.length === 1) {
    numeratorExpr = numTerms[0]!;
  } else {
    numeratorExpr = mul(...numTerms);
  }

  // Build denominator expression
  const denomTerms: Expr[] = [];
  for (const factor_ of denomFactored.factors) {
    const factorExpr = factor_.factor.toExpr();
    if (factor_.multiplicity === 1) {
      denomTerms.push(factorExpr);
    } else {
      denomTerms.push(pow(factorExpr, num(factor_.multiplicity)));
    }
  }

  if (denomTerms.length === 0) {
    return numeratorExpr;
  }

  let denominatorExpr: Expr;
  if (denomTerms.length === 1) {
    denominatorExpr = denomTerms[0]!;
  } else {
    denominatorExpr = mul(...denomTerms);
  }

  // Return as numerator * denominator^(-1)
  return mul(numeratorExpr, pow(denominatorExpr, num(-1)));
}

/**
 * Collect all boundary points (zeros and asymptotes)
 */
function collectBoundaries(
  numFactored: ReturnType<typeof factor>,
  denomFactored: ReturnType<typeof factor>,
  criticalPoints: { x: Fraction; kind: 'zero' | 'asymptote' }[],
): {
  all: Fraction[];
  zeros: Fraction[];
  asymptotes: Fraction[];
} {
  const zeros: Fraction[] = [];
  const asymptotes: Fraction[] = [];

  // Extract roots from numerator
  for (const factor_ of numFactored.factors) {
    const roots = extractPolynomialRoots(factor_.factor);
    for (const root of roots) {
      zeros.push(root);
      criticalPoints.push({ x: root, kind: 'zero' });
    }
  }

  // Extract roots from denominator
  for (const factor_ of denomFactored.factors) {
    const roots = extractPolynomialRoots(factor_.factor);
    for (const root of roots) {
      asymptotes.push(root);
      criticalPoints.push({ x: root, kind: 'asymptote' });
    }
  }

  return {
    all: [...zeros, ...asymptotes],
    zeros,
    asymptotes,
  };
}

/**
 * Extract roots from a polynomial (linear factors)
 */
function extractPolynomialRoots(poly: Polynomial): Fraction[] {
  const roots: Fraction[] = [];

  if (poly.degree === 0) {
    // Constant
    return roots;
  }

  if (poly.degree === 1) {
    // ax + b => x = -b/a
    const b = poly.coeffs[0]!;
    const a = poly.coeffs[1]!;
    roots.push(new Fraction(-b.num, b.den).div(a));
    return roots;
  }

  // For higher degrees, use rational root theorem or numerical approximation
  // For now, return empty (should be factored to linear)
  return roots;
}

/**
 * Build the sign chart
 */
function buildSignChart(
  numFactored: ReturnType<typeof factor>,
  denomFactored: ReturnType<typeof factor>,
  columnBoundaries: Fraction[],
  _boundaries: { all: Fraction[]; zeros: Fraction[]; asymptotes: Fraction[] },
  _varName: string,
): SignChart {
  const rowLabels: { factor: Expr; kind: 'factor' | 'asymptote' }[] = [];
  const numFactorsWithMult: Array<{ poly: Polynomial; multiplicity: number }> = [];
  const denomFactorsWithMult: Array<{ poly: Polynomial; multiplicity: number }> = [];

  // Collect numerator factors with their multiplicities
  for (const factor_ of numFactored.factors) {
    if (factor_.factor.degree > 0) {
      rowLabels.push({ factor: factor_.factor.toExpr(), kind: 'factor' });
      numFactorsWithMult.push({ poly: factor_.factor, multiplicity: factor_.multiplicity });
    }
  }

  // Collect denominator factors with their multiplicities
  for (const factor_ of denomFactored.factors) {
    if (factor_.factor.degree > 0) {
      rowLabels.push({ factor: factor_.factor.toExpr(), kind: 'asymptote' });
      denomFactorsWithMult.push({ poly: factor_.factor, multiplicity: factor_.multiplicity });
    }
  }

  // Build cells ROW-MAJOR: one row per rowLabel (numerator factors then
  // denominator factors, matching the order rowLabels was populated in
  // above), one entry per region. This matches the renderer's contract
  // (SignChartOptions.cells: "rows × (boundaries.length + 1) columns") —
  // it indexes cells[rowIdx][colIdx].
  const numIntervals = columnBoundaries.length + 1;
  const midpoints: Fraction[] = [];
  for (let colIdx = 0; colIdx < numIntervals; colIdx++) {
    midpoints.push(getIntervalMidpoint(columnBoundaries, colIdx));
  }

  const cells: SignCell[][] = [];

  for (const { poly: numFactor } of numFactorsWithMult) {
    cells.push(midpoints.map((midpoint) => fractionToSign(numFactor.evaluate(midpoint))));
  }

  for (const { poly: denomFactor } of denomFactorsWithMult) {
    cells.push(midpoints.map((midpoint) => fractionToSign(denomFactor.evaluate(midpoint))));
  }

  // Build product row, accounting for factor multiplicity.
  // Read cells row-major too: cells[rowIdx][colIdx].
  const productRow: SignCell[] = [];
  for (let colIdx = 0; colIdx < numIntervals; colIdx++) {
    let product: -1 | 0 | 1 = 1;

    // Process numerator factors with multiplicity
    for (let i = 0; i < numFactorsWithMult.length; i++) {
      const cell = cells[i]![colIdx]!;
      const multiplicity = numFactorsWithMult[i]!.multiplicity;

      if (cell === '0') {
        product = 0;
      } else if (cell === '+') {
        // Positive sign: no change regardless of multiplicity
      } else if (cell === '-') {
        // Negative sign: only odd multiplicity flips the sign
        if (multiplicity % 2 === 1) {
          product = (product * -1) as -1 | 1;
        }
      }
    }

    // Process denominator factors with multiplicity
    for (let i = 0; i < denomFactorsWithMult.length; i++) {
      const cell = cells[numFactorsWithMult.length + i]![colIdx]!;
      const multiplicity = denomFactorsWithMult[i]!.multiplicity;

      if (cell === 'undef') {
        // Asymptote: skip
      } else if (cell === '0') {
        product = 0;
      } else if (cell === '+') {
        // No change
      } else if (cell === '-') {
        // Only odd multiplicity flips the sign
        if (multiplicity % 2 === 1) {
          product = (product * -1) as -1 | 1;
        }
      }
    }

    if (product === 1) productRow.push('+');
    else if (product === -1) productRow.push('-');
    else productRow.push('0');
  }

  return {
    rowLabels,
    columnBoundaries,
    cells,
    productRow,
  };
}

/**
 * Get the midpoint of an interval region for sign testing
 */
function getIntervalMidpoint(boundaries: Fraction[], regionIndex: number): Fraction {
  if (regionIndex === 0) {
    // Region before first boundary: use boundary - 1
    if (boundaries.length === 0) {
      return new Fraction(0, 1);
    }
    return boundaries[0]!.sub(new Fraction(1, 1));
  }

  if (regionIndex === boundaries.length) {
    // Region after last boundary: use boundary + 1
    return boundaries[boundaries.length - 1]!.add(new Fraction(1, 1));
  }

  // Region between boundaries: use midpoint
  const left = boundaries[regionIndex - 1]!;
  const right = boundaries[regionIndex]!;
  return new Fraction(left.num * right.den + right.num * left.den, 2 * left.den * right.den);
}

/**
 * Convert a Fraction to a sign character
 */
function fractionToSign(frac: Fraction): SignCell {
  const sign = frac.sign;
  if (sign === 0) return '0';
  if (sign === 1) return '+';
  return '-';
}

/**
 * Read off intervals from the sign chart
 */
function readOffIntervals(
  chart: SignChart,
  columnBoundaries: Fraction[],
  boundaries: { all: Fraction[]; zeros: Fraction[]; asymptotes: Fraction[] },
  comparator: Comparator,
): Interval[] {
  const intervals: Interval[] = [];

  // Determine which signs match the comparator
  const matchingSigns: SignCell[] = [];
  if (comparator === '>' || comparator === '>=') {
    matchingSigns.push('+');
    if (comparator === '>=') {
      matchingSigns.push('0');
    }
  } else {
    matchingSigns.push('-');
    if (comparator === '<=') {
      matchingSigns.push('0');
    }
  }

  // Process each column (region)
  const numIntervals = columnBoundaries.length + 1;
  let inRegion = false;
  let regionStart: Fraction | null = null;

  for (let colIdx = 0; colIdx < numIntervals; colIdx++) {
    const sign = chart.productRow[colIdx]!;
    const matches = matchingSigns.includes(sign);

    // For strict inequalities (> or <), if we're at a boundary between two matching regions
    // that is a zero, force a region break.
    // This ensures (x-1)^2 > 0 splits at x=1.
    let forceBoundaryBreak = false;
    if ((comparator === '>' || comparator === '<') && matches && inRegion && colIdx > 0) {
      // Get the boundary between previous region (colIdx-1) and current region (colIdx)
      const boundaryFrac = columnBoundaries[colIdx - 1];
      if (boundaryFrac) {
        const isZero = boundaries.zeros.some((z) => z.equals(boundaryFrac));
        const isAsymptote = boundaries.asymptotes.some((a) => a.equals(boundaryFrac));
        // If the boundary is a zero (not an asymptote) and both regions match, split
        if (isZero && !isAsymptote && chart.productRow[colIdx - 1] === sign) {
          forceBoundaryBreak = true;
        }
      }
    }

    if (matches && !inRegion && !forceBoundaryBreak) {
      // Start of a matching region
      inRegion = true;
      regionStart = colIdx === 0 ? null : (columnBoundaries[colIdx - 1] ?? null);
    } else if ((!matches || forceBoundaryBreak) && inRegion) {
      // End of a matching region
      inRegion = false;
      const regionEnd = colIdx === 0 ? null : (columnBoundaries[colIdx - 1] ?? null);

      const interval = buildIntervalFromBoundaries(regionStart, regionEnd, boundaries, comparator);
      if (interval) intervals.push(interval);

      // If we forced a break, start a new region on the other side
      if (forceBoundaryBreak && matches) {
        inRegion = true;
        regionStart = columnBoundaries[colIdx - 1] ?? null;
      }
    }
  }

  // If still in a region at the end
  if (inRegion) {
    const regionEnd = null; // Unbounded above
    const interval = buildIntervalFromBoundaries(regionStart, regionEnd, boundaries, comparator);
    if (interval) intervals.push(interval);
  }

  return intervals;
}

/**
 * Build an interval from boundaries, considering zeros vs asymptotes
 */
function buildIntervalFromBoundaries(
  left: Fraction | null,
  right: Fraction | null,
  boundaries: { all: Fraction[]; zeros: Fraction[]; asymptotes: Fraction[] },
  comparator: Comparator,
): Interval | null {
  let start: Expr;
  let openLeft: boolean;

  if (left === null) {
    start = NEGATIVE_INFINITY;
    openLeft = true;
  } else {
    start = num(left);
    // Check if left is an asymptote (always open) or zero (closed for >= / <=)
    const isAsymptote = boundaries.asymptotes.some((b) => b.equals(left));
    if (isAsymptote) {
      openLeft = true;
    } else {
      openLeft = !(comparator === '>=' || comparator === '<=');
    }
  }

  let end: Expr;
  let openRight: boolean;

  if (right === null) {
    end = POSITIVE_INFINITY;
    openRight = true;
  } else {
    end = num(right);
    const isAsymptote = boundaries.asymptotes.some((b) => b.equals(right));
    if (isAsymptote) {
      openRight = true;
    } else {
      openRight = !(comparator === '>=' || comparator === '<=');
    }
  }

  return { start, end, openLeft, openRight };
}

/**
 * Intersect multiple interval lists
 */
export function intervalIntersection(intervalLists: readonly (readonly Interval[])[]): Interval[] {
  if (intervalLists.length === 0) return [];

  let result = Array.from(intervalLists[0]!);

  for (let i = 1; i < intervalLists.length; i++) {
    result = intersectTwoIntervalLists(result, Array.from(intervalLists[i]!));
  }

  return result;
}

/**
 * Intersect two interval lists
 */
function intersectTwoIntervalLists(list1: Interval[], list2: Interval[]): Interval[] {
  const result: Interval[] = [];

  for (const int1 of list1) {
    for (const int2 of list2) {
      const intersection = intersectTwoIntervals(int1, int2);
      if (intersection) {
        result.push(intersection);
      }
    }
  }

  return mergeAdjacentIntervals(result);
}

/**
 * Intersect two individual intervals
 */
function intersectTwoIntervals(int1: Interval, int2: Interval): Interval | null {
  // Find the max of the starts and min of the ends
  const startCmp = compareExpressions(int1.start, int2.start);
  const start = startCmp > 0 ? int1.start : int2.start;
  // When starts are equal, take the MORE RESTRICTIVE (open wins)
  const openLeft =
    startCmp > 0 ? int1.openLeft : startCmp < 0 ? int2.openLeft : int1.openLeft || int2.openLeft;

  const endCmp = compareExpressions(int1.end, int2.end);
  const end = endCmp < 0 ? int1.end : int2.end;
  // When ends are equal, take the MORE RESTRICTIVE (open wins)
  const openRight =
    endCmp < 0 ? int1.openRight : endCmp > 0 ? int2.openRight : int1.openRight || int2.openRight;

  // Check if valid interval (start <= end, strict if open)
  const cmp = compareExpressions(start, end);
  if (cmp > 0) return null;
  if (cmp === 0 && (openLeft || openRight)) return null; // Empty point interval

  return { start, end, openLeft, openRight };
}

/**
 * Union multiple interval lists
 */
export function intervalUnion(intervalLists: readonly (readonly Interval[])[]): Interval[] {
  const allIntervals: Interval[] = [];

  for (const list of intervalLists) {
    allIntervals.push(...list);
  }

  return mergeAdjacentIntervals(allIntervals);
}

/**
 * Merge adjacent or overlapping intervals
 */
function mergeAdjacentIntervals(intervals: Interval[]): Interval[] {
  if (intervals.length === 0) return [];

  // Sort intervals by start point
  const sorted = intervals.sort((a, b) => compareExpressions(a.start, b.start));

  const merged: Interval[] = [];
  let current = sorted[0]!;

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i]!;

    // Check if current and next should merge
    const currentEndCmp = compareExpressions(current.end, next.start);
    if (currentEndCmp > 0 || (currentEndCmp === 0 && !(current.openRight && next.openLeft))) {
      // Merge: extend current to include next
      const endCmp = compareExpressions(current.end, next.end);
      current = {
        start: current.start,
        end: endCmp >= 0 ? current.end : next.end,
        openLeft: current.openLeft,
        openRight: endCmp >= 0 ? current.openRight : next.openRight,
      };
    } else {
      merged.push(current);
      current = next;
    }
  }

  merged.push(current);
  return merged;
}

/**
 * Compare two expressions numerically
 * Returns: -1 if a < b, 0 if a = b, 1 if a > b
 */
function compareExpressions(a: Expr, b: Expr): -1 | 0 | 1 {
  // Handle infinities
  if (isNegativeInfinity(a) && isNegativeInfinity(b)) return 0;
  if (isPositiveInfinity(a) && isPositiveInfinity(b)) return 0;
  if (isNegativeInfinity(a)) return -1;
  if (isNegativeInfinity(b)) return 1;
  if (isPositiveInfinity(a)) return 1;
  if (isPositiveInfinity(b)) return -1;

  // Both should be numbers
  if (a.kind === 'num' && b.kind === 'num') {
    return a.value.cmp(b.value);
  }

  // Fallback
  return 0;
}
